/** Data-fetching hook for subscription / plan-distribution metrics.
 *
 * Uses react-query so data is cached across tab switches.
 * Fetches the plan-distribution snapshot endpoint.
 */

import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest, isApiError } from "@/shared/lib/apiClient";
import { queryKeys } from "@/shared/lib/queryKeys";

import type { MetricsRange, PlanDistributionResponse } from "../lib/metricsTypes";
import { rangeToKey, rangeToQueryParams } from "../lib/metricsTypes";

interface UseSubscriptionsResult {
  planDistribution: PlanDistributionResponse | null;
  loading: boolean;
  error: string | null;
  range: MetricsRange;
  setRange: (range: MetricsRange) => void;
  refetch: () => void;
}

export function useSubscriptions(initialRange: MetricsRange = "30d"): UseSubscriptionsResult {
  const [range, setRange] = useState<MetricsRange>(initialRange);
  const queryClient = useQueryClient();
  const rangeKey = rangeToKey(range);
  const qp = rangeToQueryParams(range);

  const { data, isLoading, error } = useQuery<PlanDistributionResponse>({
    queryKey: queryKeys.planDistribution(rangeKey),
    queryFn: async () => {
      try {
        return await apiRequest<PlanDistributionResponse>("GET", `/api/metrics/plan-distribution?${qp}`);
      } catch (err) {
        if (isApiError(err) && err.status === 400) {
          return { plans: [], total_users: 0, trial_users: 0, expired_trial_users: 0, subscribed_users: 0 };
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["metrics", "plan-distribution"] });
  }, [queryClient]);

  return {
    planDistribution: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    range,
    setRange,
    refetch,
  };
}
